import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Bot, X, Send, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const AI_GOLD = '#D4A017';

const renderBold = (text) => text.split(/\*\*([^*]+?)\*\*/g).map((part, i) => (i % 2 ? <strong key={i}>{part}</strong> : part));

const Bubble = ({ role, content, isDarkMode, index }) => {
  const mine = role === 'user';
  return (
    <div data-testid={`ai-chat-message-${index}`} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
      <div style={{
        maxWidth: '88%', padding: '0.6rem 0.8rem', borderRadius: mine ? '12px 12px 2px 12px' : '12px 12px 12px 2px',
        background: mine ? AI_GOLD : (isDarkMode ? '#1a1a2e' : '#f7f3e6'),
        color: mine ? '#1a1300' : (isDarkMode ? '#f7fafc' : '#2d3748'),
        fontSize: '0.87rem', lineHeight: 1.55, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        border: mine ? 'none' : `1px solid ${isDarkMode ? '#2d3748' : '#eadfba'}`
      }}>{mine ? content : renderBold(content)}</div>
    </div>
  );
};

const CreditBadge = ({ usage, isDarkMode }) => {
  if (!usage) return null;
  if (usage.unlimited) {
    return <span data-testid="ai-chat-credits" style={{ fontSize: '0.75rem', color: AI_GOLD }}>Perguntas ilimitadas</span>;
  }
  const out = usage.remaining === 0;
  return (
    <span data-testid="ai-chat-credits" style={{
      fontSize: '0.75rem', fontWeight: 600, whiteSpace: 'nowrap', padding: '0.2rem 0.55rem', borderRadius: '999px',
      background: out ? 'rgba(239,68,68,0.15)' : 'rgba(212,160,23,0.15)', color: out ? '#ef4444' : (isDarkMode ? '#f6d365' : '#8a6500')
    }}>
      {usage.remaining} de {usage.limit} perguntas hoje
    </span>
  );
};

export const BookAIChat = ({ bookId, token, isDarkMode, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [usage, setUsage] = useState(null);
  const [hasText, setHasText] = useState(true);
  const [question, setQuestion] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [model, setModel] = useState('');
  const endRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/digital/books/${bookId}/ai-chat?auth=${token}`)
      .then(({ data }) => { setMessages(data.messages); setUsage(data); setHasText(data.has_text); setModel(`${data.provider} ${data.model}`); })
      .catch((e) => setError(e.response?.data?.detail || 'Não foi possível abrir o assistente'))
      .finally(() => setLoading(false));
  }, [bookId, token]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const ask = async () => {
    const q = question.trim();
    if (q.length < 2 || sending) return;
    setError(''); setSending(true); setQuestion('');
    setMessages((m) => [...m, { role: 'user', content: q }, { role: 'assistant', content: '' }]);
    if (usage && !usage.unlimited) setUsage((u) => ({ ...u, remaining: Math.max(0, u.remaining - 1) }));
    try {
      const res = await fetch(`${API}/digital/books/${bookId}/ai-chat?auth=${token}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ question: q })
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw Object.assign(new Error(body.detail || 'Erro ao enviar pergunta'), { refund: true });
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split('\n\n');
        buf = parts.pop();
        for (const part of parts) {
          if (!part.startsWith('data: ')) continue;
          const ev = JSON.parse(part.slice(6));
          if (ev.type === 'delta') {
            setMessages((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + ev.content }; return c; });
          } else {
            setUsage(ev);
            if (ev.type === 'error') throw new Error(ev.detail);
          }
        }
      }
    } catch (e) {
      setError(e.message);
      setMessages((m) => (m[m.length - 1]?.content ? m : m.slice(0, -2)));
      if (e.refund) {
        axios.get(`${API}/digital/books/${bookId}/ai-chat?auth=${token}`).then(({ data }) => setUsage(data)).catch(() => {});
      }
    } finally {
      setSending(false);
    }
  };

  const disabled = sending || !hasText || (usage && !usage.unlimited && usage.remaining === 0);
  const border = isDarkMode ? '#2d3748' : '#e2e8f0';

  return (
    <aside data-testid="ai-chat-panel" className="ai-chat-panel" style={{
      width: '380px', flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0,
      background: isDarkMode ? '#0f0f1a' : 'white', borderLeft: `1px solid ${border}`, animation: 'fadeIn 0.25s ease-out'
    }}>
      <style>{`@media (max-width: 768px) { .ai-chat-panel { position: fixed; inset: 0; width: 100vw !important; z-index: 6000; } }`}</style>
      <div style={{ padding: '0.9rem 1rem', borderBottom: `1px solid ${border}`, background: isDarkMode ? '#1a1a2e' : '#fffaf0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, color: isDarkMode ? 'white' : '#2d3748', margin: 0 }}>
            <Bot className="w-5 h-5" style={{ color: AI_GOLD }} /> Assistente do livro
          </h3>
          <button onClick={onClose} data-testid="ai-chat-close-btn" title="Fechar"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: isDarkMode ? '#cbd5e0' : '#718096' }}>
            <X className="w-5 h-5" />
          </button>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
          <span style={{ fontSize: '0.72rem', color: isDarkMode ? '#a0aec0' : '#718096' }}>Responde só com o conteúdo deste livro</span>
          <CreditBadge usage={usage} isDarkMode={isDarkMode} />
        </div>
      </div>

      <div data-testid="ai-chat-messages" style={{ flex: 1, overflowY: 'auto', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        {loading && <Loader2 className="w-5 h-5 animate-spin" style={{ color: AI_GOLD, margin: '2rem auto' }} />}
        {!loading && !hasText && (
          <p data-testid="ai-chat-no-text" style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', textAlign: 'center' }}>
            Este livro não possui texto pesquisável, por isso o assistente não está disponível.
          </p>
        )}
        {!loading && hasText && messages.length === 0 && (
          <p data-testid="ai-chat-empty" style={{ fontSize: '0.85rem', color: isDarkMode ? '#cbd5e0' : '#718096', textAlign: 'center', marginTop: '1.5rem' }}>
            Pergunte qualquer coisa sobre este livro. Ex.: "Qual é a ideia principal do capítulo 2?"
          </p>
        )}
        {messages.map((m, i) => (
          m.role === 'assistant' && !m.content
            ? <div key={i} data-testid="ai-chat-typing" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.8rem', color: AI_GOLD }}><Loader2 className="w-4 h-4 animate-spin" /> Consultando o livro…</div>
            : <Bubble key={i} index={i} role={m.role} content={m.content} isDarkMode={isDarkMode} />
        ))}
        <div ref={endRef} />
      </div>

      {error && <p data-testid="ai-chat-error" style={{ margin: '0 1rem 0.5rem', fontSize: '0.8rem', color: '#ef4444' }}>{error}</p>}
      {hasText && usage && !usage.unlimited && usage.remaining === 0 && !error && (
        <p data-testid="ai-chat-limit-reached" style={{ margin: '0 1rem 0.5rem', fontSize: '0.8rem', color: isDarkMode ? '#f6d365' : '#8a6500' }}>
          Você usou as {usage.limit} perguntas de hoje. Novas perguntas liberadas à meia-noite (horário de Brasília).
        </p>
      )}

      {model && (
        <p data-testid="ai-chat-model-info" style={{ margin: '0 1rem 0.4rem', fontSize: '0.7rem', color: isDarkMode ? '#a0aec0' : '#718096', textAlign: 'center' }}>
          Tecnologia ChatGPT · {model}
        </p>
      )}
      <div style={{ padding: '0.75rem', borderTop: `1px solid ${border}`, display: 'flex', gap: '0.5rem' }}>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value.slice(0, 1000))}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
          placeholder="Digite sua pergunta…"
          disabled={disabled}
          rows={2}
          data-testid="ai-chat-input"
          style={{
            flex: 1, resize: 'none', padding: '0.55rem 0.7rem', borderRadius: '8px', fontSize: '0.87rem',
            border: `1px solid ${isDarkMode ? '#4a5568' : '#e2e8f0'}`, background: isDarkMode ? '#1a1a2e' : 'white', color: isDarkMode ? '#fff' : '#2d3748'
          }}
        />
        <button onClick={ask} disabled={disabled || question.trim().length < 2} data-testid="ai-chat-send-btn" title="Enviar"
          style={{
            width: '44px', borderRadius: '8px', border: 'none', background: AI_GOLD, color: '#1a1300',
            cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled || question.trim().length < 2 ? 0.5 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'opacity 0.2s ease'
          }}>
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </aside>
  );
};
